
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SmartSwitchInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Smart Switch Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional smart switch installation in Fresno, CA. Replace traditional switches with voice-controlled, app-enabled smart switches with scheduling and dimming.',
    url: `${siteUrl}/services/smart-home-energy/smart-switch-installation`,
  };

  return (
    <>
      <SEOHead
        title="Smart Switch Installation Fresno CA | Voice Controlled Light Switches"
        description="Smart switch installation in Fresno, CA. Replace old switches with voice-controlled, app-enabled smart switches. Scheduling, dimming, and remote control. Licensed electrician."
        canonical="/services/smart-home-energy/smart-switch-installation"
        keywords="smart switch installation Fresno, voice controlled switches, smart dimmer switches, automated light switches"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Smart Switch Installation — Fresno, CA"
        heroSubtitle="Upgrade your Fresno home with intelligent lighting control"
        heroImage="https://public.readdy.ai/ai/img_res/14683946c78ee00cd2c143f0be2197b2.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Smart%20dimmer%20switch%20panel%20with%20LED%20indicators%20installed%20on%20modern%20white%20wall%20showing%20multiple%20switches%20with%20smartphone%20app%20control%20interface%20visible%20contemporary%20home%20interior%20no%20people%20clean%20design&width=700&height=600&seq=smartswitch-fresno-01&orientation=portrait"
        fresnoContent="Fresno homeowners are replacing traditional light switches with smart switches that offer voice control, remote access, and automated scheduling. Unlike smart bulbs, smart switches control any light fixture and work even when the switch is off. Perfect for Fresno's climate, you can automate outdoor lighting for security, schedule ceiling fans for hot summer days, and control every light in your home from your smartphone. We serve all Fresno areas including Woodward Park, Fig Garden, Tower District, and surrounding neighborhoods with professional smart switch installations."
        features={[
          {
            icon: 'ri-toggle-line',
            title: 'Smart Switch Replacement',
            description: 'Replace any traditional switch with smart control',
          },
          {
            icon: 'ri-contrast-2-line',
            title: 'Smart Dimmer Installation',
            description: 'Adjust brightness with voice or app',
          },
          {
            icon: 'ri-voice-recognition-line',
            title: 'Voice Control Integration',
            description: 'Works with Alexa, Google, and Siri',
          },
          {
            icon: 'ri-calendar-check-line',
            title: 'Automated Scheduling',
            description: 'Lights follow your daily routine',
          },
          {
            icon: 'ri-smartphone-line',
            title: 'Remote Access',
            description: 'Control lights from anywhere',
          },
          {
            icon: 'ri-group-line',
            title: 'Multi-Way Switch Setup',
            description: 'Smart control for 3-way and 4-way switches',
          },
        ]}
        whatsIncluded={[
          'Complete electrical assessment of existing switches',
          'Verification of neutral wire availability',
          'Professional removal of old switches',
          'Smart switch installation with proper wiring',
          'Wi-Fi network connection and configuration',
          'Voice assistant integration (Alexa, Google, Siri)',
          'Mobile app setup and user training',
          'Automated schedule creation',
          'Multi-switch grouping and scene setup',
          'Complete testing and ongoing support',
        ]}
        faqs={[
          {
            question: 'Do I need special wiring for smart switches in my Fresno home?',
            answer:
              "Most smart switches require a neutral wire, which is standard in Fresno homes built after 1985. Older homes may not have neutral wires in all switch boxes. During our assessment, we check your existing wiring and can add neutral wires where needed. Some smart switches (like Lutron Caseta) don't require neutral wires, making them perfect for older Fresno homes. All our work is performed by licensed electricians and meets California electrical codes.",
          },
          {
            question: 'Can smart switches work with my existing light fixtures in Fresno?',
            answer:
              'Yes! Smart switches work with any light fixture—LED, incandescent, fluorescent, or halogen. Unlike smart bulbs that require specific fixtures, smart switches control the power to any fixture. This makes them ideal for Fresno homes with ceiling fans, chandeliers, recessed lighting, or outdoor fixtures. We ensure proper load compatibility during installation, and smart dimmers work beautifully with dimmable LED bulbs common in modern Fresno homes.',
          },
          {
            question: 'How much do smart switches cost to install in Fresno?',
            answer:
              "Smart switch installation in Fresno typically costs $150-$250 per switch including the device and professional installation. Smart dimmer switches run $175-$300 per switch. For whole-home installations (15-20 switches), we offer package pricing that reduces the per-switch cost. The investment pays off through energy savings, increased home value, and the convenience of automated lighting—especially valuable during Fresno's hot summers when you want to minimize unnecessary heat from lights.",
          },
          {
            question: 'Will smart switches work during internet outages in Fresno?',
            answer:
              'Yes! Smart switches function as regular switches even without internet—you can still turn lights on/off manually. Voice control and remote access require internet, but local control always works. Many smart switches also support Bluetooth control, allowing smartphone access even without Wi‑Fi. During Fresno power outages, smart switches resume their programmed settings once power is restored, and most remember their last state.',
          },
          {
            question: 'Can you install smart switches for 3-way lights in Fresno homes?',
            answer:
              "Absolutely! We specialize in 3-way and 4-way smart switch installations common in Fresno homes with staircases, hallways, and large rooms. Some systems require a smart switch at one location and a companion switch at others. Other systems (like Lutron Caseta) use wireless remotes that mount over existing switches. We assess your specific setup and recommend the best solution for your Fresno home's layout.",
          },
          {
            question: 'How long does smart switch installation take in a Fresno home?',
            answer:
              'Installing a single smart switch takes 30-45 minutes including wiring verification, installation, and app setup. For a typical Fresno home with 15-20 switches, whole‑home installation takes 1-2 days. We work efficiently, test each switch thoroughly, and set up automated schedules and voice control before we leave. Most homeowners can use their home normally during installation, and we clean up completely when finished.',
          },
        ]}
        ctaText="Schedule Your Smart Switch Installation"
      />
    </>
  );
}
